export type NavItem = {
  href: string;
  label: string;
  description?: string;
  external?: boolean;
};

export type NavGroup = {
  id: string;
  label: string;
  blurb: string;
  items: NavItem[];
};

/** Canonical enterprise information architecture for datasyte.ai. */
export const NAV_GROUPS: NavGroup[] = [
  {
    id: "platform",
    label: "Platform",
    blurb: "The datasyteAI™ intelligence layer and the AiMediQ™ Code Cart it powers.",
    items: [
      {
        href: "/platform",
        label: "Platform Overview",
        description: "How the intelligence layer, cart, and workflow fit together.",
      },
      {
        href: "/datasyteai",
        label: "datasyteAI™",
        description: "The clinical intelligence layer trained to your protocols.",
      },
      {
        href: "/aimediq",
        label: "AiMediQ™",
        description: "The AI-powered Code Cart designed for the bedside.",
      },
      {
        href: "/why-pathfree",
        label: "Why PathFree",
        description: "Category comparison and the design commitments behind the platform.",
      },
      {
        href: "/how-it-works",
        label: "How It Works",
        description: "The resuscitation workflow, step by step.",
      },
      {
        href: "/experience",
        label: "Experience the Command Center",
        description: "An interactive, synthetic walkthrough of a simulated event.",
      },
    ],
  },
  {
    id: "solutions",
    label: "Solutions",
    blurb: "Built for the teams accountable for resuscitation quality.",
    items: [
      {
        href: "/solutions",
        label: "Solutions Overview",
        description: "Find the workflow that matches your role.",
      },
      {
        href: "/solutions/hospitals-health-systems",
        label: "Hospitals and Health Systems",
        description: "Standardize response quality across units and campuses.",
      },
      {
        href: "/solutions/resuscitation-teams",
        label: "Resuscitation Teams",
        description: "Next-step prompts, timing cues, and role clarity.",
      },
      {
        href: "/solutions/quality-patient-safety",
        label: "Quality and Patient Safety",
        description: "Reviewable event records and protocol adherence signals.",
      },
      {
        href: "/solutions/pharmacy-clinical-operations",
        label: "Pharmacy and Clinical Operations",
        description: "Cart readiness, medication visibility, and restock discipline.",
      },
      {
        href: "/solutions/it-security-integration",
        label: "IT, Security, and Integration",
        description: "Architecture, data handling, and integration approach.",
      },
      {
        href: "/solutions/training-preparedness",
        label: "Training and Preparedness",
        description: "Simulation, drills, and readiness between real events.",
      },
    ],
  },
  {
    id: "evidence",
    label: "Evidence",
    blurb: "Our clinical approach, validation work, and regulatory posture.",
    items: [
      {
        href: "/evidence",
        label: "Evidence Overview",
        description: "How our evidence program is structured and what we do not claim.",
      },
      {
        href: "/evidence/program",
        label: "Evidence Program",
        description: "The five-stage evidence pipeline with verified status for every activity.",
      },
      {
        href: "/evidence/clinical-approach",
        label: "Clinical Approach",
        description: "Guideline alignment and clinician-in-the-loop design.",
      },
      {
        href: "/evidence/research-publications",
        label: "Research and Publications",
        description: "Publications, abstracts, and study records with full citations.",
      },
      {
        href: "/evidence/regulatory-quality",
        label: "Regulatory and Quality",
        description: "Development status, quality practices, risk, security, and governance.",
      },
      {
        href: "/responsible-ai",
        label: "Responsible Clinical AI",
        description: "Human oversight, governance, data stewardship, human factors, and limitations.",
      },
    ],
  },
  {
    id: "enterprise",
    label: "Enterprise",
    blurb: "What evaluation, security, and procurement teams ask for.",
    items: [
      {
        href: "/trust-center",
        label: "Trust Center",
        description: "Security, privacy, quality, regulatory, and continuity practices in one place.",
      },
      {
        href: "/enterprise-deployment",
        label: "Enterprise Deployment",
        description: "The intended framework for deploying across a hospital or health system.",
      },
      {
        href: "/solutions/it-security-integration",
        label: "IT, Security, and Integration",
        description: "Architecture, data handling, and the integration approach.",
      },
      {
        href: "/readiness-index",
        label: "Resuscitation Readiness Index",
        description: "A 20-question educational self-assessment with a maturity profile.",
      },
      {
        href: "/value-model",
        label: "Resuscitation Operations Value Model",
        description: "An illustrative model for executives — adjust every assumption.",
      },
      {
        href: "/partnerships",
        label: "Strategic Partnerships",
        description: "Eleven engagement tracks, the partnership process, and the inquiry form.",
      },
    ],
  },
  {
    id: "resources",
    label: "Resources",
    blurb: "Reading, watching, and reference material for evaluation teams.",
    items: [
      {
        href: "/resources",
        label: "Resource Center",
        description: "Search insights, clinical education, research summaries, and papers.",
      },
      {
        href: "/resources/insights",
        label: "Insights",
        description: "Perspectives on resuscitation quality and decision support.",
      },
      {
        href: "/institute",
        label: "PathFree Institute",
        description: "Our educational publishing program — briefings, perspectives, and frameworks.",
      },
      {
        href: "/institute/state-of-intelligent-resuscitation",
        label: "The State of Intelligent Resuscitation",
        description: "The flagship annual publication framework and its methodology standard.",
      },
      {
        href: "/blog",
        label: "Blog",
        description: "Clinical perspectives, product updates, and company news.",
      },
      {
        href: "/newsroom",
        label: "Newsroom and Media Center",
        description: "Verified releases, coverage, awards, and the press kit for journalists.",
      },
      {
        href: "/newsroom/press-kit",
        label: "Press Kit",
        description: "Approved logos, headshots, boilerplate, and brand-usage guidance.",
      },
      { href: "/video", label: "Videos", description: "Short explainers, including what a Code Blue is." },
      {
        href: "/resources/downloads",
        label: "Downloads",
        description: "Overview materials and the interactive flip book.",
      },
      {
        href: "/resources/news", label: "News", description: "Company and program updates.",
      },
      {
        href: "/glossary",
        label: "Glossary",
        description: "Definitions for resuscitation and clinical-AI terminology.",
      },
      {
        href: "/resources/faq",
        label: "Frequently Asked Questions",
        description: "Straight answers on status, scope, and safety.",
      },

    ],

  },
  {
    id: "company",
    label: "Company",
    blurb: "PathFree Technologies Corporation — the company behind both brands.",
    items: [
      {
        href: "/company/about",
        label: "About PathFree",
        description: "Mission, why PathFree, brand architecture, and verified milestones.",
      },
      {
        href: "/pathfree-technologies",
        label: "PathFree Technologies Corporation",
        description: "Company overview and frequently asked questions about PathFree.",
      },

      {
        href: "/company/leadership",
        label: "Leadership and Advisors",
        description: "Executives, clinical, regulatory, and strategic advisors.",
      },
      {
        href: "/company/partnerships",
        label: "Recognition and Partnerships",
        description: "Partnership tracks and our display standard for logos and awards.",
      },
      {
        href: "/company/news",
        label: "News and Media",
        description: "Press releases, coverage, appearances, and announcements.",
      },
      { href: "/contact", label: "Contact", description: "Talk to us about evaluation or a demo." },
    ],
  },
];

export const DEMO_URL = "https://pathfree.com/contact";
export const PLATFORM_URL = "/platform";

export const LEGAL_LINKS: NavItem[] = [
  { href: "/privacy", label: "Privacy Policy" },
  { href: "/terms", label: "Terms of Service" },
  { href: "/accessibility", label: "Accessibility" },
  { href: "/trust-center", label: "Trust Center" },
  { href: "/trademarks", label: "Trademarks" },
  { href: "/disclaimers", label: "Disclaimers and Regulatory Notices" },
];

/** Verified PathFree social profiles only. */
export const SOCIAL_LINKS = [
  { href: "https://www.linkedin.com/in/pathfreetech/", label: "PathFree on LinkedIn", icon: "linkedin" as const },
  { href: "https://x.com/PathFreeTech", label: "PathFree on X", icon: "x" as const },
  { href: "https://www.youtube.com/channel/UCgedxHJUM_TIIKmJWo0B19w", label: "PathFree on YouTube", icon: "youtube" as const },
];

export const COMPANY_STATEMENT =
  "datasyteAI™ and AiMediQ™ are developed by PathFree Technologies Corporation, Irvine, California.";

export const isExternalHref = (href: string) =>
  href.startsWith("http://") || href.startsWith("https://") || href.startsWith("mailto:");

/** Flat lookup of every in-app nav destination, used for breadcrumbs. */
export const navLookup: Record<string, { group: string; label: string }> = Object.fromEntries(
  NAV_GROUPS.flatMap((group) =>
    group.items
      .filter((item) => !isExternalHref(item.href))
      .map((item) => [item.href, { group: group.label, label: item.label }]),
  ),
);
